'use client';

import { CheckCircle, XCircle, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

interface ToolResultCardProps {
  result: {
    tool_name: string;
    success: boolean;
    result_summary: string;
    error?: string;
  };
  timestamp: string;
}

export function ToolResultCard({ result, timestamp }: ToolResultCardProps) {
  const { t } = useTranslation();
  const Icon = result.success ? CheckCircle : XCircle;

  return (
    <div className="max-w-[80%] space-y-1">
      <div
        className={cn(
          'rounded-lg border p-3',
          result.success
            ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'
            : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950',
        )}
      >
        {/* Header */}
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-medium">
            <Trans i18nKey="chat:toolResult.title" />
          </span>
          <Icon
            className={cn(
              'ml-auto h-4 w-4',
              result.success
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400',
            )}
          />
        </div>

        {/* Result summary */}
        <p className="mt-2 text-sm">{result.result_summary}</p>

        {/* Error details */}
        {result.error && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
            {result.error}
          </p>
        )}

        {/* Tool name */}
        <p className="text-muted-foreground mt-1 text-xs">
          {t('chat:toolResult.tool', { name: result.tool_name })}
        </p>
      </div>

      {/* Timestamp */}
      <div className="text-muted-foreground text-xs">{timestamp}</div>
    </div>
  );
}
